import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface QueryLogEntry {
  id: number;
  conversationId?: string | null;
  userQuery: string;
  retrievalQuery?: string | null;
  fullPrompt?: string | null;
  response: string;
  intent?: string | null;
  route?: string | null;
  tokensIn: number;
  tokensOut: number;
  totalTokens?: number;
  totalCost?: number;
  costCurrency?: string;
  model: string;
  ai: string;
  createdAt?: string;
}


const QueryLog: React.FC = () => {
  const token = localStorage.getItem('token');
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
  const [logs, setLogs] = useState<QueryLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalPrompt, setModalPrompt] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState('');
  const [debouncedConversationId, setDebouncedConversationId] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedConversationId(conversationId.trim()), 300);
    return () => clearTimeout(t);
  }, [conversationId]);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('No authentication token found');
          return;
        }
        const params = new URLSearchParams();
        if (debouncedConversationId) params.set('conversation_id', debouncedConversationId);

        const res = await api.get(`/api/querylog${params.toString() ? `?${params.toString()}` : ''}`, {
          headers: authHeaders,
        });
        setLogs(res.data.logs || []);
      } catch (e: any) {
        console.error('Error fetching query logs:', e);
        setError(e.response?.data?.message || e.message || 'Failed to fetch logs');
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [debouncedConversationId]);

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Query History</h2>
      <div className="mb-4">
        <label className="block text-xs font-semibold text-gray-600 mb-1">Conversation ID Filter</label>
        <input
          value={conversationId}
          onChange={(e) => setConversationId(e.target.value)}
          placeholder="Type conversation ID..."
          className="w-full max-w-md rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
        />
      </div>
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <div className="text-lg text-gray-600">Loading query history...</div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          <strong>Error:</strong> {error}
        </div>
      ) : logs.length === 0 ? (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg">
          No queries found. Start asking questions in the Playground to see your history here!
        </div>
      ) : (
        <div className="overflow-x-auto shadow-lg rounded-lg border border-gray-200">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">User Query</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">AI Prompt</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Response</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Conversation ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Tokens In</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Tokens Out</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Total Tokens</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Cost</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Intent</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Route</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Model</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">AI Provider</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Time</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 border-b">
                    <div className="max-w-xs overflow-hidden">
                      <div className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                        {log.userQuery?.length > 100 ? `${log.userQuery.substring(0, 100)}...` : log.userQuery}
                      </div>
                      <div className="mt-2 text-xs text-gray-500 whitespace-pre-wrap break-words">
                        <span className="font-semibold text-gray-600">Retrieval:</span>{' '}
                        {log.retrievalQuery
                          ? (log.retrievalQuery.length > 100
                              ? `${log.retrievalQuery.substring(0, 100)}...`
                              : log.retrievalQuery)
                          : '-'}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 border-b cursor-pointer" onClick={() => log.fullPrompt && setModalPrompt(log.fullPrompt)}>
                    <div className="max-w-xs overflow-hidden">
                      <div className="text-sm text-blue-900 whitespace-pre-wrap break-words underline">
                        {log.fullPrompt && log.fullPrompt.length > 100 ? `${log.fullPrompt.substring(0, 100)}... (click to view)` : (log.fullPrompt || '—')}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 border-b">
                    <div className="max-w-xs overflow-hidden">
                      <div className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                        {log.response.length > 100 ? `${log.response.substring(0, 100)}...` : log.response}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900 border-b">{log.conversationId || '-'}</td>
                  <td className="px-4 py-4 text-sm text-gray-900 border-b">{log.tokensIn}</td>
                  <td className="px-4 py-4 text-sm text-gray-900 border-b">{log.tokensOut}</td>
                  <td className="px-4 py-4 text-sm text-gray-900 border-b">{log.totalTokens ?? (log.tokensIn + log.tokensOut)}</td>
                  <td className="px-4 py-4 text-sm text-gray-900 border-b">
                    {typeof log.totalCost === 'number' ? `${log.costCurrency || 'USD'} ${log.totalCost.toFixed(6)}` : '-'}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900 border-b">{log.intent || '-'}</td>
                  <td className="px-4 py-4 text-sm text-gray-900 border-b">{log.route || '-'}</td>
                  <td className="px-4 py-4 text-sm text-gray-900 border-b">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {log.model}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900 border-b">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {log.ai}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-500 border-b">
                    {log.createdAt ? new Date(log.createdAt).toLocaleString() : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Modal for full AI prompt */}
          {modalPrompt && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
              <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full p-6 relative">
                <button
                  className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-2xl"
                  onClick={() => setModalPrompt(null)}
                  aria-label="Close"
                >
                  &times;
                </button>
                <h3 className="text-lg font-bold mb-4 text-gray-800">Full AI Prompt</h3>
                <pre className="whitespace-pre-wrap break-words text-sm text-gray-900 max-h-96 overflow-y-auto">{modalPrompt}</pre>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QueryLog;
